class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.addEventListener('click', this.onCheckoutClick.bind(this));
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this._isSyncingTotalPrice = false;
    this.setHeaderCartIconAccessibility();
    this.syncTotalPriceDisplay();
    this.observeTotalPriceClassMutations();
  }

  onCheckoutClick(event) {
    const checkoutButton = event.target.closest('#CartDrawer-Checkout');
    if (!checkoutButton) return;
    if (checkoutButton.classList.contains('loading')) return;

    const formId = checkoutButton.getAttribute('form');
    const form = (formId && document.getElementById(formId)) || checkoutButton.form || document.getElementById('CartDrawer-Form');
    if (!form) return;

    checkoutButton.classList.add('loading');
    checkoutButton.setAttribute('aria-busy', 'true');
    event.preventDefault();

    requestAnimationFrame(() => {
      let checkoutInput = form.querySelector('input[name="checkout"]');
      if (!checkoutInput) {
        checkoutInput = document.createElement('input');
        checkoutInput.type = 'hidden';
        checkoutInput.name = 'checkout';
        form.appendChild(checkoutInput);
      }
      checkoutInput.value = 'Checkout';
      checkoutButton.disabled = true;
      form.submit();
    });
  }

  syncTotalPriceDisplay() {
    if (this._isSyncingTotalPrice) return;

    this._isSyncingTotalPrice = true;
    try {
      this.normalizeTotalPriceClasses();
      this.applyTotalPriceFormatting();
    } finally {
      this._isSyncingTotalPrice = false;
    }
  }

  normalizeTotalPriceClasses() {
    const saleEl = this.querySelector('.totals__total-value [class*="cart-drawer-total-sale"]');
    if (saleEl && saleEl.className !== 'pi-cart-drawer-total-sale') {
      saleEl.className = 'pi-cart-drawer-total-sale';
    }

    const compareEl = this.querySelector('.totals__total-value [class*="cart-drawer-total-compare"]');
    if (compareEl && compareEl.className !== 'pi-cart-drawer-total-compare') {
      compareEl.className = 'pi-cart-drawer-total-compare';
    }
  }

  applyTotalPriceFormatting() {
    const totalsEl = this.querySelector('.totals');
    if (!totalsEl) return;

    const useCurrencyCode = String(totalsEl.dataset.currencyCodeEnabled) === 'true';
    const valueKey = useCurrencyCode ? 'moneyWithCurrency' : 'money';
    const saleEl = this.querySelector('.pi-cart-drawer-total-sale');
    const compareEl = this.querySelector('.pi-cart-drawer-total-compare');

    if (saleEl && saleEl.dataset[valueKey] && saleEl.textContent.trim() !== saleEl.dataset[valueKey]) {
      saleEl.textContent = saleEl.dataset[valueKey];
    }

    if (compareEl && compareEl.dataset[valueKey] && compareEl.textContent.trim() !== compareEl.dataset[valueKey]) {
      compareEl.textContent = compareEl.dataset[valueKey];
    }
  }

  observeTotalPriceClassMutations() {
    if (this._totalPriceObserver) return;

    let rafId = null;
    this._totalPriceObserver = new MutationObserver(() => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => this.syncTotalPriceDisplay());
    });

    this._totalPriceObserver.observe(this, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      if (cartLink.tagName === 'A') event.preventDefault();
      this.open(cartLink);
    });
    if (cartLink.tagName === 'A') {
      cartLink.setAttribute('role', 'button');
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    }
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    this.syncTotalPriceDisplay();
    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
